import React, { useState, useEffect } from 'react';
import { Text, VStack, UploadZone, useBoolean } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { SmtFile } from '../../../../../../../types/Form';
import { CreateSignedDistUploadId } from '../../../../../../common/src/legacy/services/apiRouterService';
import { FieldError } from 'react-hook-form';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';
import LinkItem, {
  LinkEditValues,
} from '@flows/src/components/input/company-scan-inputs/links/LinkItem';

interface Props {
  value?: SmtFile | null;
  onChange: (file: SmtFile | null) => void;
  error?: FieldError;
  isDisabled?: boolean;
  withLink?: boolean;
  isEditable?: boolean;
  supportedLinkFormats?: string[];
}

export const supportedLinkFormatsFallback = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
];

const LinkInput: React.FC<React.PropsWithChildren<Props>> = ({
  value = null,
  onChange,
  error,
  isDisabled,
  isEditable,
  supportedLinkFormats = supportedLinkFormatsFallback,
  withLink,
}) => {
  const { distribution } = useAuth();
  const { t } = useTranslation();
  const [file, setFile] = useState<File>();
  const [isUploading, setIsUploading] = useBoolean();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(error?.message);

  useEffect(() => {
    setErrorMsg(error?.message);
  }, [error]);

  const uploadFile = async (file: File) => {
    const [resp, status] = await CreateSignedDistUploadId(distribution?.id, {
      filename: file.name,
    });
    if (status !== 200) {
      try {
        await fetch(resp.link, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/pdf' },
          body: new Blob([file], { type: file.type }),
        });

        return {
          document_id: resp.document_id,
          location: resp.location,
          filename: file.name,
          size: file.size,
          filetype: file.type,
          description: undefined,
          public_bucket_link: resp.public_bucket_link,
          public_url: resp.public_url,
        };
      } catch (err) {
        return Promise.reject();
      }
    } else {
      return Promise.reject();
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setIsUploading.on();
      setErrorMsg(undefined);
      setFile(file);

      const newFile = await uploadFile(file);
      onChange(newFile);
    } catch {
      setErrorMsg(t('Upload of a document has failed'));
    } finally {
      setIsUploading.off();
    }
  };

  const onRemove = () => {
    setFile(undefined);
    setErrorMsg(undefined);

    // Update answers state
    onChange(null);
  };

  const fetchFile = async (_file: SmtFile) => {
    if (_file.public_bucket_link) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await fetch(_file.public_bucket_link as any);
      const responseBlob = await response.blob();
      const newFile = new File([responseBlob], _file.filename, {
        type: response.headers.get('Content-Type') || '',
      });
      setFile(newFile);
    }
  };

  useEffect(() => {
    if (value) fetchFile(value);
  }, [value]);

  const handleEditFile = (formValues: LinkEditValues) => {
    if (!value) return;
    onChange({ ...value, ...formValues });
  };

  return (
    <>
      <VStack align="stretch" spacing="2">
        {value ? (
          <LinkItem
            file={file}
            document={value}
            onRemove={onRemove}
            onSubmit={(values) => handleEditFile(values)}
            isEditable={isEditable}
          />
        ) : (
          <UploadZone
            onFilesAccepted={(newFile: File) => {
              handleUpload(newFile);
            }}
            accept={supportedLinkFormats}
            isLoading={isUploading}
            isDisabled={isDisabled}
          />
        )}
        {errorMsg && <Text color="danger.main">{errorMsg}</Text>}
      </VStack>
    </>
  );
};

export default LinkInput;
